/**
 * Game trace: the engine's "debug console that says everything that happens
 * in the game" (scene loads, spawns, collisions, triggers, inputs, script logs
 * and errors, timers, animation, audio, UI, custom `trace()` facts, perf).
 *
 * Pure helpers shared by the editor (Game console panel), the server (ring
 * buffer + assistant board ops) and the worker (headless `prism run --trace`
 * files). Output is designed to be compact: one short line per event, so a
 * model can read hundreds of events inside a tool-output budget.
 */

/** One engine trace event (prism_runtime trace JSON). */
export interface TraceEvent {
  /** Increasing per Play run. */
  readonly seq: number;
  /** Frame. */
  readonly f: number;
  /** Game seconds. */
  readonly t: number;
  readonly type: string;
  readonly entity: string | null;
  readonly name: string | null;
  readonly data: Record<string, unknown>;
}

/** A trace event as held by the server: a project-wide cursor and its Play session. */
export interface StoredTraceEvent extends TraceEvent {
  /** Server cursor (monotonic per project, across Play runs). Pass as `since`. */
  readonly id: number;
  readonly session: string;
}

/** Event filter (same shape as the engine's `traceTake` filter JSON). */
export interface TraceFilter {
  /** Type prefixes ("physics", "script.error", "input.*"). */
  readonly types?: readonly string[];
  /** Entity id or name (case-insensitive substring; also matches the other party of a collision). */
  readonly entity?: string;
  /** Case-insensitive substring of the formatted line. */
  readonly text?: string;
  /** Game-seconds window. */
  readonly from?: number;
  readonly to?: number;
  readonly limit?: number;
}

/** World snapshot filter (engine `worldSnapshot`). */
export interface SnapshotFilter {
  readonly name?: string;
  readonly component?: string;
  readonly near?: readonly [number, number, number];
  readonly radius?: number;
  readonly limit?: number;
}

export const TRACE_LEVELS = ['errors', 'normal', 'verbose', 'everything'] as const;
export type TraceLevel = (typeof TRACE_LEVELS)[number];

/** Engine `traceConfig` JSON. */
export interface TraceConfig {
  readonly level: TraceLevel;
  readonly types?: Readonly<Record<string, boolean>>;
  readonly transformHz?: number;
  readonly transformEpsilon?: number;
  readonly include?: readonly string[];
  readonly exclude?: readonly string[];
  readonly bufferSize?: number;
}

/** One entity of `worldSnapshot`. */
export interface SnapshotEntity {
  readonly id: string;
  readonly name?: string;
  readonly parent?: string | null;
  readonly enabled?: boolean;
  readonly tags?: readonly string[];
  readonly layer?: string | number | null;
  readonly transform?: {
    readonly position?: readonly number[];
    readonly rotation?: readonly number[];
    readonly scale?: readonly number[];
  };
  readonly velocity?: readonly number[] | null;
  readonly components?: Record<string, unknown>;
}

export interface WorldSnapshot {
  readonly entities: readonly SnapshotEntity[];
  readonly frame?: number;
  readonly time?: number;
  readonly total?: number;
}

/** Synthetic markers the server inserts around a Play run. */
export const TRACE_RUN_START = 'play.start';
export const TRACE_RUN_STOP = 'play.stop';

/** Types that count as problems (listed first in summaries). */
export function isProblemType(type: string): boolean {
  return /(^|\.)(error|warn|panic)$/.test(type) || type === 'perf.spike';
}

/* ------------------------------------------------------------------ */
/* Parsing                                                             */
/* ------------------------------------------------------------------ */

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Coerce one parsed value into a trace event, or null. */
export function toTraceEvent(value: unknown): TraceEvent | null {
  if (!isRecord(value) || typeof value.type !== 'string') return null;
  const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  return {
    seq: num(value.seq, 0),
    f: num(value.f, 0),
    t: num(value.t, 0),
    type: value.type,
    entity: typeof value.entity === 'string' ? value.entity : null,
    name: typeof value.name === 'string' ? value.name : null,
    data: isRecord(value.data) ? value.data : {},
  };
}

/** Engine `traceTake` output (JSON array); bad input → []. */
export function parseTraceArray(json: string): TraceEvent[] {
  try {
    const raw: unknown = JSON.parse(json);
    // The engine returns {events, cursor, ...}; plain arrays are accepted too.
    const parsed: unknown =
      raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as { events?: unknown }).events : raw;
    if (!Array.isArray(parsed)) return [];
    const out: TraceEvent[] = [];
    for (const item of parsed) {
      const event = toTraceEvent(item);
      if (event) out.push(event);
    }
    return out;
  } catch {
    return [];
  }
}

/** A `prism run --trace` JSONL file; malformed lines are skipped. */
export function parseTraceJsonl(text: string): TraceEvent[] {
  const out: TraceEvent[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const event = toTraceEvent(JSON.parse(trimmed));
      if (event) out.push(event);
    } catch {
      // Partial last line of a file still being written.
    }
  }
  return out;
}

/** Tool arguments → filter. `types` may be an array or a comma list. */
export function normalizeTraceFilter(args: Record<string, unknown>): TraceFilter & { since?: number } {
  const types = Array.isArray(args.types)
    ? args.types.filter((t): t is string => typeof t === 'string' && t.trim() !== '').map((t) => t.trim())
    : typeof args.types === 'string'
      ? args.types
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const str = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);
  const filter: Mutable<TraceFilter & { since?: number }> = {};
  if (types.length) filter.types = types;
  const entity = str(args.entity);
  if (entity) filter.entity = entity;
  const text = str(args.text);
  if (text) filter.text = text;
  const from = num(args.from);
  if (from !== undefined) filter.from = from;
  const to = num(args.to);
  if (to !== undefined) filter.to = to;
  const limit = num(args.limit);
  if (limit !== undefined) filter.limit = Math.max(1, Math.min(2000, Math.floor(limit)));
  const since = num(args.since);
  if (since !== undefined) filter.since = since;
  return filter;
}

/* ------------------------------------------------------------------ */
/* Filtering                                                           */
/* ------------------------------------------------------------------ */

/** "physics" matches "physics.collision.enter"; "input.*" and "input." work too. */
export function typeMatches(type: string, prefixes: readonly string[] | undefined): boolean {
  if (!prefixes || prefixes.length === 0) return true;
  for (const raw of prefixes) {
    const prefix = raw.replace(/\.?\*$/, '');
    if (!prefix) return true;
    if (type === prefix || type.startsWith(prefix.endsWith('.') ? prefix : `${prefix}.`)) return true;
  }
  return false;
}

const OTHER_KEYS = ['other', 'otherName', 'with', 'b'] as const;

/** The other party of a two-entity event (collision, trigger), if any. */
export function otherParty(event: TraceEvent): string | null {
  for (const key of OTHER_KEYS) {
    const value = event.data[key];
    if (typeof value === 'string' && value) return value;
  }
  return null;
}

export function matchTrace(event: TraceEvent, filter: TraceFilter): boolean {
  if (!typeMatches(event.type, filter.types)) return false;
  if (filter.from !== undefined && event.t < filter.from) return false;
  if (filter.to !== undefined && event.t > filter.to) return false;
  if (filter.entity) {
    const needle = filter.entity.toLowerCase();
    const hay = [event.entity, event.name, otherParty(event), event.data.otherId as unknown];
    if (!hay.some((h) => typeof h === 'string' && h.toLowerCase().includes(needle))) return false;
  }
  if (filter.text) {
    if (!formatTraceLine(event).toLowerCase().includes(filter.text.toLowerCase())) return false;
  }
  return true;
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

/** Round floats for display ("1.2345678" → 1.235; -0 → 0). */
export function roundValue(value: unknown, depth = 0): unknown {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value);
    const r = Math.round(value * 1000) / 1000;
    return Object.is(r, -0) ? 0 : r;
  }
  if (depth > 6) return Array.isArray(value) ? '[…]' : isRecord(value) ? '{…}' : value;
  if (Array.isArray(value)) return value.map((v) => roundValue(v, depth + 1));
  if (isRecord(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = roundValue(v, depth + 1);
    return out;
  }
  return value;
}

/** Compact JSON with rounded numbers, clipped to `max` characters. */
export function compactJson(value: unknown, max = 160): string {
  let text: string;
  try {
    text = JSON.stringify(roundValue(value)) ?? '';
  } catch {
    text = String(value);
  }
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text;
}

/** Who the event is about: "player", or "player↔coin_3" for a pair. */
export function traceSubject(event: TraceEvent): string {
  const self = event.name || event.entity || '';
  const other = otherParty(event);
  return other ? `${self || '?'}↔${other}` : self;
}

/** The data part of a line (without the keys already shown as the subject). */
export function traceDataText(event: TraceEvent, max = 160): string {
  const data = event.data;
  const message = typeof data.message === 'string' ? data.message : typeof data.msg === 'string' ? data.msg : null;
  const rest: Record<string, unknown> = {};
  let restCount = 0;
  for (const [key, value] of Object.entries(data)) {
    if (OTHER_KEYS.includes(key as (typeof OTHER_KEYS)[number])) continue;
    if (message !== null && (key === 'message' || key === 'msg')) continue;
    rest[key] = value;
    restCount += 1;
  }
  const parts: string[] = [];
  if (message !== null) parts.push(JSON.stringify(message.length > max ? `${message.slice(0, max - 1)}…` : message));
  if (restCount > 0) parts.push(compactJson(rest, message !== null ? Math.max(40, max - message.length) : max));
  return parts.join(' ');
}

/** `t=12.40 f=744 physics.trigger.enter player↔coin_3 {"impulse":2.1}` */
export function formatTraceLine(event: TraceEvent, maxData = 160): string {
  const subject = traceSubject(event);
  const data = traceDataText(event, maxData);
  return `t=${event.t.toFixed(2)} f=${event.f} ${event.type}${subject ? ` ${subject}` : ''}${data ? ` ${data}` : ''}`;
}

/* ------------------------------------------------------------------ */
/* Summary                                                             */
/* ------------------------------------------------------------------ */

export interface TraceSummary {
  readonly total: number;
  readonly from: number | null;
  readonly to: number | null;
  readonly frames: readonly [number, number] | null;
  readonly byType: readonly (readonly [string, number])[];
  readonly byEntity: readonly (readonly [string, number])[];
  readonly problems: readonly TraceEvent[];
  readonly problemCount: number;
}

export function summarizeTrace(events: readonly TraceEvent[], maxProblems = 20): TraceSummary {
  const types = new Map<string, number>();
  const entities = new Map<string, number>();
  const problems: TraceEvent[] = [];
  let problemCount = 0;
  let from: number | null = null;
  let to: number | null = null;
  let f0: number | null = null;
  let f1: number | null = null;
  for (const e of events) {
    types.set(e.type, (types.get(e.type) ?? 0) + 1);
    const who = e.name || e.entity;
    if (who) entities.set(who, (entities.get(who) ?? 0) + 1);
    if (isProblemType(e.type)) {
      problemCount += 1;
      if (problems.length < maxProblems) problems.push(e);
    }
    if (e.type === TRACE_RUN_START || e.type === TRACE_RUN_STOP) continue;
    from = from === null ? e.t : Math.min(from, e.t);
    to = to === null ? e.t : Math.max(to, e.t);
    f0 = f0 === null ? e.f : Math.min(f0, e.f);
    f1 = f1 === null ? e.f : Math.max(f1, e.f);
  }
  const sorted = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return {
    total: events.length,
    from,
    to,
    frames: f0 === null || f1 === null ? null : [f0, f1],
    byType: sorted(types),
    byEntity: sorted(entities),
    problems,
    problemCount,
  };
}

export function formatTraceSummary(summary: TraceSummary, topEntities = 15): string {
  const lines: string[] = [];
  const span =
    summary.from === null ? '' : ` t=${summary.from.toFixed(2)}..${(summary.to ?? summary.from).toFixed(2)}`;
  const frames = summary.frames ? ` frames ${summary.frames[0]}..${summary.frames[1]}` : '';
  lines.push(`${summary.total} events${span}${frames}`);
  if (summary.problemCount > 0) {
    lines.push(`Errors/warnings (${summary.problemCount}${summary.problemCount > summary.problems.length ? `, first ${summary.problems.length}` : ''}):`);
    for (const e of summary.problems) lines.push(`  ${formatTraceLine(e, 300)}`);
  } else {
    lines.push('Errors/warnings: none');
  }
  if (summary.byType.length) lines.push(`By type: ${summary.byType.map(([t, n]) => `${t} ${n}`).join(', ')}`);
  if (summary.byEntity.length) {
    const shown = summary.byEntity.slice(0, topEntities);
    const more = summary.byEntity.length - shown.length;
    lines.push(`By entity: ${shown.map(([t, n]) => `${t} ${n}`).join(', ')}${more > 0 ? `, +${more} more` : ''}`);
  }
  return lines.join('\n');
}

/* ------------------------------------------------------------------ */
/* Reading a window of events (shared by server buffer and trace files) */
/* ------------------------------------------------------------------ */

export interface TraceWindow<E extends TraceEvent> {
  readonly events: readonly E[];
  /** Matching events in the searched range. */
  readonly matched: number;
  /** Matching events newer than the last one returned (only when reading forward from `since`). */
  readonly newer: number;
}

/**
 * Select events: with `since`, the oldest `limit` matches after the cursor (so
 * polling never skips anything); without it, the newest `limit` matches.
 */
export function selectTrace<E extends TraceEvent>(
  events: readonly E[],
  filter: TraceFilter & { since?: number },
  cursor: (event: E) => number,
  defaultLimit = 200,
): TraceWindow<E> {
  const limit = filter.limit ?? defaultLimit;
  const matches: E[] = [];
  for (const e of events) {
    if (filter.since !== undefined && cursor(e) <= filter.since) continue;
    if (matchTrace(e, filter)) matches.push(e);
  }
  if (filter.since !== undefined) {
    const shown = matches.slice(0, limit);
    return { events: shown, matched: matches.length, newer: matches.length - shown.length };
  }
  return { events: matches.slice(-limit), matched: matches.length, newer: 0 };
}

/* ------------------------------------------------------------------ */
/* World snapshot formatting                                           */
/* ------------------------------------------------------------------ */

function vec(v: readonly number[] | null | undefined): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  return `(${v.map((n) => (typeof n === 'number' ? String(roundValue(n)) : '?')).join(',')})`;
}

function isZero(v: readonly number[] | null | undefined): boolean {
  return !Array.isArray(v) || v.every((n) => typeof n !== 'number' || Math.abs(n) < 1e-4);
}

/** One line per entity: `player "Player" pos=(1,2,0) vel=(3,0,0) [Rigidbody,Script] tags=hero`. */
export function formatSnapshotEntity(e: SnapshotEntity): string {
  const parts = [e.id];
  if (e.name && e.name !== e.id) parts.push(JSON.stringify(e.name));
  if (e.enabled === false) parts.push('(disabled)');
  if (e.parent) parts.push(`parent=${e.parent}`);
  const pos = vec(e.transform?.position);
  if (pos) parts.push(`pos=${pos}`);
  const rot = e.transform?.rotation;
  if (rot && !isZero(rot)) parts.push(`rot=${vec(rot)}`);
  const scale = e.transform?.scale;
  if (scale && !scale.every((n) => Math.abs(n - 1) < 1e-4)) parts.push(`scale=${vec(scale)}`);
  if (e.velocity && !isZero(e.velocity)) parts.push(`vel=${vec(e.velocity)}`);
  const keys = e.components ? Object.keys(e.components) : [];
  if (keys.length) parts.push(`[${keys.join(',')}]`);
  if (e.tags && e.tags.length) parts.push(`tags=${e.tags.join(',')}`);
  if (e.layer !== undefined && e.layer !== null && e.layer !== '' && e.layer !== 0 && e.layer !== 'default')
    parts.push(`layer=${String(e.layer)}`);
  return parts.join(' ');
}

export function formatWorldSnapshot(snapshot: WorldSnapshot, maxChars = 20_000): string {
  const entities = snapshot.entities ?? [];
  const head = [
    `${entities.length}${snapshot.total !== undefined && snapshot.total !== entities.length ? ` of ${snapshot.total}` : ''} entities`,
    snapshot.time !== undefined ? `t=${snapshot.time.toFixed(2)}` : '',
    snapshot.frame !== undefined ? `f=${snapshot.frame}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const lines = [head];
  let size = head.length;
  for (let i = 0; i < entities.length; i += 1) {
    const line = formatSnapshotEntity(entities[i]!);
    if (size + line.length + 1 > maxChars) {
      lines.push(`[${entities.length - i} more entities not shown; narrow with name, component, near/radius or limit]`);
      break;
    }
    lines.push(line);
    size += line.length + 1;
  }
  return lines.join('\n');
}

/** Full detail of one entity: header line + each component as compact JSON. */
export function formatEntityState(e: SnapshotEntity, maxChars = 16_000): string {
  const lines = [formatSnapshotEntity(e)];
  let size = lines[0]!.length;
  for (const [key, value] of Object.entries(e.components ?? {})) {
    const line = `  ${key}: ${compactJson(value, 2000)}`;
    if (size + line.length > maxChars) {
      lines.push('  [more components not shown]');
      break;
    }
    lines.push(line);
    size += line.length + 1;
  }
  for (const [key, value] of Object.entries(e as unknown as Record<string, unknown>)) {
    if (['id', 'name', 'parent', 'enabled', 'tags', 'layer', 'transform', 'velocity', 'components'].includes(key)) continue;
    const line = `  ${key}: ${compactJson(value, 600)}`;
    if (size + line.length > maxChars) break;
    lines.push(line);
    size += line.length + 1;
  }
  return lines.join('\n');
}

/** Normalize `game_trace_config` tool arguments into an engine config. */
export function normalizeTraceConfig(args: Record<string, unknown>): TraceConfig {
  const level = typeof args.level === 'string' && (TRACE_LEVELS as readonly string[]).includes(args.level)
    ? (args.level as TraceLevel)
    : 'normal';
  const list = (v: unknown) =>
    Array.isArray(v)
      ? v.filter((s): s is string => typeof s === 'string' && s.trim() !== '').map((s) => s.trim())
      : typeof v === 'string'
        ? v.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined;
  const config: Mutable<TraceConfig> = { level };
  if (isRecord(args.types)) {
    const types: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(args.types)) if (typeof v === 'boolean') types[k] = v;
    config.types = types;
  } else if (Array.isArray(args.types) || typeof args.types === 'string') {
    // A list of types = turn those on.
    const types: Record<string, boolean> = {};
    for (const t of list(args.types) ?? []) types[t] = true;
    config.types = types;
  }
  if (typeof args.transformHz === 'number' && Number.isFinite(args.transformHz))
    config.transformHz = Math.max(0, Math.min(240, args.transformHz));
  if (typeof args.transformEpsilon === 'number' && Number.isFinite(args.transformEpsilon))
    config.transformEpsilon = Math.max(0, args.transformEpsilon);
  const include = list(args.include);
  if (include) config.include = include;
  const exclude = list(args.exclude);
  if (exclude) config.exclude = exclude;
  if (typeof args.bufferSize === 'number' && Number.isFinite(args.bufferSize))
    config.bufferSize = Math.max(100, Math.min(1_000_000, Math.floor(args.bufferSize)));
  return config;
}

/** Live editor requests the server can make over the event socket. */
export type EditorRequestOp = 'worldSnapshot' | 'entityState' | 'traceConfig' | 'profile';
